import { observable, computed, action, makeAutoObservable } from 'mobx';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { FilterOptionType, FilterValueType } from 'src/components/CmsCasinoManagement/SearchAndFilters/FilterDropdown';
import { AppCasinoState } from 'src/domains/casino/state/AppCasino.state';
import {
    FILTER_ALL,
    GameStudioType,
    ProviderGameType,
    CollectionType,
    ProviderType,
} from 'src/domains/casino/models/models';
import { casinoProviderUtils } from 'src/domains/casino/utils/CasinoProviderUtils';

export type LoadingProviderInfoType = {
    provider: ProviderType;
    providerName: string;
};

export class GamesPageState {
    @observable public searchValue: string = '';
    @observable public selectedProviderId: FilterValueType = null;
    @observable public selectedStudioId: FilterValueType = null;

    public constructor(
        private readonly appCasinoState: AppCasinoState,
        private readonly configState: ConfigState,
        private readonly collection: CollectionType
    ) {
        makeAutoObservable(this);
    }

    private get providersListToFetch(): Array<ProviderType> {
        // TODO: Remove before develop bingo
        if (this.collection === 'bingo') {
            return [];
        }
        const providersList: Array<ProviderType> = this.configState.config.casinoProviders[this.collection].sort(
            (a, b) => a.localeCompare(b)
        );
        return providersList;
    }

    @computed public get loadingProviders(): Array<LoadingProviderInfoType> {
        const loading: Array<LoadingProviderInfoType> = [];

        for (const provider of this.providersListToFetch) {
            const games = this.appCasinoState.providerStore.getProviderData(provider, this.collection);
            const collectionGames = games.getCollectionGames(this.collection);

            if (collectionGames === 'loading') {
                loading.push({
                    provider,
                    providerName: casinoProviderUtils.convertSlugToName(provider),
                });
            }
        }

        return loading;
    }

    @computed private get providersListToDisplay(): Array<{ provider: ProviderType; providerName: string }> {
        const list: Map<
            string,
            {
                provider: ProviderType;
                providerName: string;
            }
        > = new Map();
        if (this.collectionGames === 'loading') {
            return [];
        }

        for (const game of this.collectionGames) {
            if (list.has(game.provider) === false) {
                list.set(game.provider, { provider: game.provider, providerName: game.providerName });
            }
        }
        return Array.from(list.values()).sort((a, b) => a.providerName.localeCompare(b.providerName));
    }

    @computed private get collectionGames(): Array<ProviderGameType> | 'loading' {
        const gamesList: Array<ProviderGameType> = [];
        let hasReadyProvider = false;
        let hasLoadingProvider = false;

        for (const provider of this.providersListToFetch) {
            const games = this.appCasinoState.providerStore.getProviderData(provider, this.collection);
            const collectionGames = games.getCollectionGames(this.collection);

            if (collectionGames === 'loading') {
                hasLoadingProvider = true;
                continue;
            }

            hasReadyProvider = true;

            for (const game of collectionGames) {
                gamesList.push(game);
            }
        }

        if (hasReadyProvider === false && hasLoadingProvider) {
            return 'loading';
        }

        return gamesList.sort((a, b) => a.providerName.localeCompare(b.providerName));
    }

    @computed private get gamesFilteredByProvider(): Array<ProviderGameType> | 'loading' {
        const games = this.collectionGames;

        if (games === 'loading') {
            return 'loading';
        }

        return games.filter((game) => {
            if (this.selectedProviderId !== null && game.provider !== this.selectedProviderId) {
                return false;
            }

            return true;
        });
    }

    @computed private get gamesFilteredByStudio(): Array<ProviderGameType> | 'loading' {
        const games = this.gamesFilteredByProvider;

        if (games === 'loading') {
            return 'loading';
        }

        return games.filter((game) => {
            if (this.selectedStudioId !== null && game.studio.name !== this.selectedStudioId) {
                return false;
            }

            return true;
        });
    }

    @computed public get filteredGames(): Array<ProviderGameType> | 'loading' {
        const games = this.gamesFilteredByStudio;

        if (games === 'loading') {
            return 'loading';
        }

        return games.filter((game) => {
            if (this.searchValue.length > 2 && !game.name.toLowerCase().includes(this.searchValue.toLowerCase())) {
                return false;
            }

            return true;
        });
    }

    // Filters
    public onSearchGame = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.searchValue = event.target.value;
    };

    @computed public get providerFilterOptions(): Array<FilterOptionType> {
        const providersListOut: Array<FilterOptionType> = [
            {
                value: FILTER_ALL,
                label: FILTER_ALL,
            },
        ];

        for (const provider of this.providersListToDisplay) {
            providersListOut.push({
                label: provider.providerName,
                value: provider.provider,
            });
        }

        return providersListOut;
    }

    @action public onProviderFilterChange = (selectedValue: FilterValueType): void => {
        this.selectedProviderId = selectedValue === FILTER_ALL ? null : selectedValue;
        this.selectedStudioId = null;
    };

    private filterAndSortStudioList = (studioList: Array<GameStudioType>): Array<GameStudioType> => {
        const studiosWithoutDuplicates = studioList.filter(
            (value, index, self) => index === self.findIndex((t) => t.name === value.name)
        );

        const sortedStudioList = studiosWithoutDuplicates.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
            if (a.name > b.name) {
                return 1;
            }
            return 0;
        });

        return sortedStudioList;
    };

    @computed private get gamesStudios(): Array<GameStudioType> {
        if (this.gamesFilteredByProvider !== 'loading') {
            const studios: Array<GameStudioType> = [];

            this.gamesFilteredByProvider.map((game: ProviderGameType) => {
                if (
                    game.studio.id !== '' &&
                    game.studio.name !== '' &&
                    game.studio.name.toLocaleLowerCase() !== game.providerName.toLocaleLowerCase()
                ) {
                    studios.push({
                        id: game.studio.name,
                        name: game.studio.name,
                    });
                }
            });
            return this.filterAndSortStudioList(studios);
        }
        return [];
    }

    @computed public get studioFilterOptions(): Array<FilterOptionType> {
        const studioListOut: Array<FilterOptionType> = [
            {
                value: FILTER_ALL,
                label: FILTER_ALL,
            },
        ];

        for (const studio of this.gamesStudios) {
            studioListOut.push({
                label: studio.name,
                value: studio.id,
            });
        }

        return studioListOut;
    }

    @action public onStudioFilterChange = (selectedValue: FilterValueType): void => {
        this.selectedStudioId = selectedValue === FILTER_ALL ? null : selectedValue;
    };
}
